/**
 * textureUtils.ts — shared utilities for JS texture scripts.
 *
 * Used by both the runtime texture system (useTextureScripts) and the
 * preview dialog (JsTexturePreviewDialog) to parse script metadata.
 */

export const DEFAULT_TEXTURE_WIDTH = 512;
export const DEFAULT_TEXTURE_HEIGHT = 512;

export type ScriptMetadata = {
  width: number;
  height: number;
  /**
   * Target frame rate for the texture script.
   *
   *   undefined  — uncapped; script drives its own rAF loop at full speed (~60 fps)
   *   0          — static; the first frame is sent then sendFrame() becomes a no-op.
   *                Receiving a PARAMS message re-enables one redraw so param-driven
   *                static textures (e.g. labels) still update on slider changes.
   *   1..N       — throttled; sendFrame() is rate-limited to N fps. Extra calls
   *                within the current frame interval are silently dropped.
   */
  fps: number | undefined;
};

/**
 * Parse metadata exported from a JS texture script:
 *   export const width  = NNN;
 *   export const height = NNN;
 *   export const fps    = N;    // optional; 0 = static, undefined = uncapped
 *
 * Returns defaults for any field not found.
 */
export function parseScriptMetadata(code: string): ScriptMetadata {
  const widthMatch = code.match(/export\s+const\s+width\s*=\s*(\d+)\s*;/);
  const heightMatch = code.match(/export\s+const\s+height\s*=\s*(\d+)\s*;/);
  const fpsMatch = code.match(/export\s+const\s+fps\s*=\s*(\d+(?:\.\d+)?)\s*;/);
  return {
    width: widthMatch ? parseInt(widthMatch[1], 10) : DEFAULT_TEXTURE_WIDTH,
    height: heightMatch ? parseInt(heightMatch[1], 10) : DEFAULT_TEXTURE_HEIGHT,
    fps: fpsMatch ? parseFloat(fpsMatch[1]) : undefined,
  };
}

/**
 * Returns true when a JS file looks like a shader script rather than a
 * texture script.  We detect this by checking for the characteristic exports
 * that only shader scripts produce.
 *
 * A file is considered a shader script when it contains both:
 *   export const vertexShader = ...
 *   export const fragmentShader = ...
 *
 * This is intentionally lenient (substring match) so it works on both
 * minified and prettily-formatted scripts.
 */
export function isShaderScript(code: string): boolean {
  return (
    /export\s+const\s+vertexShader\s*=/.test(code) &&
    /export\s+const\s+fragmentShader\s*=/.test(code)
  );
}

/**
 * @deprecated Use parseScriptMetadata instead.
 * Kept for backwards compatibility — returns only width and height.
 */
export function parseScriptDimensions(code: string): {
  width: number;
  height: number;
} {
  const { width, height } = parseScriptMetadata(code);
  return { width, height };
}
